import {
  Button, Tag, message, Breadcrumb,
  Input, Badge, Popover, Form, Select, Divider, Typography,
} from 'antd';
import {
  MedicineBoxOutlined, HomeOutlined, FilterOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useState, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { checkupApi }      from '../../../api/checkup.api';
import { registrationApi } from '../../../api/registration.api';
import { eventApi }        from '../../../api/event.api';
import DataTable    from '../../../components/common/DataTable';
import CheckupForm  from '../components/CheckupForm';
import { formatDate } from '../../../utils/helpers';
import useDebounce  from '../../../hooks/useDebounce';

const { Text } = Typography;

const ORAL_COLOR = { good: 'green', fair: 'orange', poor: 'red' };

// ─── Reusable section toolbar ──────────────────────────────────────
const SectionToolbar = ({
  title, searchValue, onSearchChange, searchPlaceholder,
  filterOpen, onFilterOpenChange, filterContent, activeFilterCount,
}) => (
  <div style={{ marginBottom: 12 }}>
    <Text strong style={{ fontFamily: "'Fredoka One', sans-serif", color: 'var(--wl-primary)', fontSize: 15 }}>
      {title}
    </Text>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, marginTop: 10,
      background: '#fff', borderRadius: 14, padding: '10px 16px',
      boxShadow: '0 2px 8px rgba(var(--wl-primary-rgb),0.08)', border: '1px solid var(--wl-primary-pale)',
    }}>
      <Input
        prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)', fontSize: 14 }} />}
        placeholder={searchPlaceholder}
        value={searchValue}
        onChange={onSearchChange}
        allowClear
        style={{ flex: 1, maxWidth: 300, borderRadius: 9999, borderColor: 'var(--wl-primary-pale)' }}
      />
      <Popover
        open={filterOpen}
        onOpenChange={onFilterOpenChange}
        trigger="click"
        placement="bottomRight"
        overlayInnerStyle={{
          borderRadius: 14, padding: '14px 16px 12px',
          boxShadow: '0 8px 32px rgba(var(--wl-primary-rgb),0.18)', border: '1px solid var(--wl-primary-pale)',
        }}
        content={filterContent}
      >
        <Badge count={activeFilterCount} color="var(--wl-primary)" size="small">
          <Button
            icon={<FilterOutlined />}
            style={{
              borderColor: activeFilterCount ? 'var(--wl-primary)' : undefined,
              color:       activeFilterCount ? 'var(--wl-primary)' : undefined,
              fontWeight:  activeFilterCount ? 600 : 400,
            }}
          >
            Filters
          </Button>
        </Badge>
      </Popover>
    </div>
  </div>
);

// ─── Pending filter panel ──────────────────────────────────────────
const PendingFilterPanel = ({ form, eventOptions, onApply, onReset }) => (
  <div style={{ width: 260, padding: '4px 0 0' }}>
    <Form form={form} layout="vertical" size="middle">
      <Form.Item name="event" label={<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wl-primary)' }}>Event</span>} style={{ marginBottom: 4 }}>
        <Select placeholder="All Events" allowClear showSearch optionFilterProp="label" options={eventOptions} style={{ width: '100%' }} />
      </Form.Item>
    </Form>
    <Divider style={{ margin: '12px 0 10px' }} />
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <Button size="small" onClick={onReset}>Reset</Button>
      <Button size="small" type="primary" icon={<FilterOutlined />} onClick={onApply}>Apply</Button>
    </div>
  </div>
);

// ─── Completed filter panel — based on CheckupForm fields ──────────
const CompletedFilterPanel = ({ form, eventOptions, onApply, onReset }) => (
  <div style={{ width: 270, padding: '4px 0 0' }}>
    <Form form={form} layout="vertical" size="middle">
      <Form.Item name="event" label={<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wl-primary)' }}>Event</span>} style={{ marginBottom: 12 }}>
        <Select placeholder="All Events" allowClear showSearch optionFilterProp="label" options={eventOptions} style={{ width: '100%' }} />
      </Form.Item>

      {/* Oral Hygiene Status — direct CheckupForm field */}
      <Form.Item name="oralHygiene" label={<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wl-primary)' }}>Oral Hygiene Status</span>} style={{ marginBottom: 12 }}>
        <Select placeholder="All" allowClear style={{ width: '100%' }}
          options={[
            { label: '🟢 Good', value: 'good' },
            { label: '🟡 Fair', value: 'fair' },
            { label: '🔴 Poor', value: 'poor' },
          ]}
        />
      </Form.Item>

      {/* Treatment Required — direct CheckupForm field */}
      <Form.Item name="treatmentRequired" label={<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wl-primary)' }}>Treatment Required</span>} style={{ marginBottom: 12 }}>
        <Select placeholder="All" allowClear style={{ width: '100%' }}
          options={[
            { label: '✅ Required',     value: 'yes' },
            { label: '❌ Not Required', value: 'no' },
          ]}
        />
      </Form.Item>

      {/* Cavities — direct CheckupForm field */}
      <Form.Item name="cavities" label={<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wl-primary)' }}>Cavities</span>} style={{ marginBottom: 4 }}>
        <Select placeholder="All" allowClear style={{ width: '100%' }}
          options={[
            { label: '⚠️ Has Cavities',    value: 'yes' },
            { label: '✅ No Cavities',     value: 'no' },
          ]}
        />
      </Form.Item>
    </Form>
    <Divider style={{ margin: '12px 0 10px' }} />
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <Button size="small" onClick={onReset}>Reset</Button>
      <Button size="small" type="primary" icon={<FilterOutlined />} onClick={onApply}>Apply</Button>
    </div>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────
const CheckupList = () => {
  const queryClient = useQueryClient();
  const navigate    = useNavigate();

  // ── Pending section state ──────────────────────────────────────
  const [pendingSearch,      setPendingSearch]      = useState('');
  const [pendingFilterOpen,  setPendingFilterOpen]  = useState(false);
  const [pendingFilters,     setPendingFilters]     = useState({});
  const [pendingPagination,  setPendingPagination]  = useState({ page: 1, limit: 10 });
  const [pendingFilterForm]  = Form.useForm();

  // ── Completed section state ────────────────────────────────────
  const [completedSearch,     setCompletedSearch]     = useState('');
  const [completedFilterOpen, setCompletedFilterOpen] = useState(false);
  const [completedFilters,    setCompletedFilters]    = useState({});
  const [completedPagination, setCompletedPagination] = useState({ page: 1, limit: 10 });
  const [completedFilterForm] = Form.useForm();

  const [checkupOpen,          setCheckupOpen]          = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  const debouncedPending   = useDebounce(pendingSearch,   300);
  const debouncedCompleted = useDebounce(completedSearch, 300);

  // ── Queries ────────────────────────────────────────────────────
  const { data: regData, isLoading: regLoading } = useQuery({
    queryKey: ['registrations-pending', debouncedPending, pendingPagination],
    queryFn:  () => registrationApi.getAll({ search: debouncedPending, isChecked: false, ...pendingPagination }),
  });

  const { data: checkupData, isLoading: checkupLoading } = useQuery({
    queryKey: ['checkups', debouncedCompleted, completedPagination],
    queryFn:  () => checkupApi.getAll({ search: debouncedCompleted, ...completedPagination }),
  });

  const { data: eventsData } = useQuery({
    queryKey: ['events-for-checkup-filter'],
    queryFn:  () => eventApi.getAll({ limit: 200 }),
  });

  // ── Mutations ──────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: checkupApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['checkups']);
      queryClient.invalidateQueries(['registrations-pending']);
      message.success('Checkup recorded successfully');
      setCheckupOpen(false);
      setSelectedRegistration(null);
    },
    onError: (err) => message.error(err.message),
  });

  // ── Search handlers ────────────────────────────────────────────
  const handlePendingSearch = useCallback((e) => {
    setPendingSearch(e.target.value);
    setPendingPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handleCompletedSearch = useCallback((e) => {
    setCompletedSearch(e.target.value);
    setCompletedPagination((p) => ({ ...p, page: 1 }));
  }, []);

  // ── Pending filter handlers ────────────────────────────────────
  const handlePendingFilterOpen = (open) => {
    if (open) { pendingFilterForm.setFieldsValue(pendingFilters); }
    setPendingFilterOpen(open);
  };

  const applyPendingFilters = () => {
    const vals  = pendingFilterForm.getFieldsValue();
    const clean = Object.fromEntries(Object.entries(vals).filter(([, v]) => v !== undefined && v !== null && v !== ''));
    setPendingFilters(clean);
    setPendingPagination((p) => ({ ...p, page: 1 }));
    setPendingFilterOpen(false);
  };

  const resetPendingFilters = () => {
    pendingFilterForm.resetFields();
    setPendingFilters({});
    setPendingPagination((p) => ({ ...p, page: 1 }));
    setPendingFilterOpen(false);
  };

  // ── Completed filter handlers ──────────────────────────────────
  const handleCompletedFilterOpen = (open) => {
    if (open) { completedFilterForm.setFieldsValue(completedFilters); }
    setCompletedFilterOpen(open);
  };

  const applyCompletedFilters = () => {
    const vals  = completedFilterForm.getFieldsValue();
    const clean = Object.fromEntries(Object.entries(vals).filter(([, v]) => v !== undefined && v !== null && v !== ''));
    setCompletedFilters(clean);
    setCompletedPagination((p) => ({ ...p, page: 1 }));
    setCompletedFilterOpen(false);
  };

  const resetCompletedFilters = () => {
    completedFilterForm.resetFields();
    setCompletedFilters({});
    setCompletedPagination((p) => ({ ...p, page: 1 }));
    setCompletedFilterOpen(false);
  };

  // ── Derived data ───────────────────────────────────────────────
  const allPending   = regData?.data      || [];
  const allCheckups  = checkupData?.data  || [];
  const pendingMeta  = regData?.meta      || {};
  const checkupMeta  = checkupData?.meta  || {};

  const eventOptions = useMemo(() => (eventsData?.data || []).map((e) => ({ label: e.title, value: e.id })), [eventsData]);

  // Client-side filter for pending
  const filteredPending = useMemo(() => allPending.filter((r) => {
    if (pendingFilters.event) {
      const eid = r.event?.id || r.event;
      if (eid !== pendingFilters.event) { return false; }
    }
    return true;
  }), [allPending, pendingFilters]);

  // Client-side filter for completed (based on CheckupForm fields)
  const filteredCheckups = useMemo(() => allCheckups.filter((c) => {
    if (completedFilters.event) {
      const eid = c.event?.id || c.event;
      if (eid !== completedFilters.event) { return false; }
    }
    if (completedFilters.oralHygiene    && c.oralHygieneStatus !== completedFilters.oralHygiene)    { return false; }
    if (completedFilters.treatmentRequired === 'yes' && !c.treatmentRequired)  { return false; }
    if (completedFilters.treatmentRequired === 'no'  &&  c.treatmentRequired)  { return false; }
    if (completedFilters.cavities          === 'yes' && !c.hasCavities)        { return false; }
    if (completedFilters.cavities          === 'no'  &&  c.hasCavities)        { return false; }
    return true;
  }), [allCheckups, completedFilters]);

  const pendingActiveCount   = Object.keys(pendingFilters).length;
  const completedActiveCount = Object.keys(completedFilters).length;

  // ── Columns ────────────────────────────────────────────────────
  const pendingColumns = [
    { title: 'Child',        key: 'child', render: (_, r) => r.child?.name        || '—' },
    { title: 'Age',          key: 'age',   render: (_, r) => r.child?.age ? `${r.child.age} yrs` : '—' },
    { title: 'Event',        key: 'event', render: (_, r) => r.event?.title        || '—', ellipsis: true },
    { title: 'Parent Phone', key: 'phone', render: (_, r) => r.child?.parent?.phone || '—' },
    { title: 'Reg. Date',    dataIndex: 'registrationDate', key: 'date', render: (v) => formatDate(v) },
    {
      title: 'Action', key: 'actions', fixed: 'right', width: 110,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<MedicineBoxOutlined />}
          style={{ borderRadius: 9999 }}
          onClick={() => { setSelectedRegistration(record); setCheckupOpen(true); }}
        >
          Checkup
        </Button>
      ),
    },
  ];

  const checkupColumns = [
    { title: 'Child',  key: 'child',  render: (_, r) => r.child?.name    || '—' },
    { title: 'Event',  key: 'event',  render: (_, r) => r.event?.title   || '—', ellipsis: true },
    { title: 'Doctor', key: 'doctor', render: (_, r) => r.doctor?.name   || '—' },
    {
      title: 'Oral Hygiene', dataIndex: 'oralHygieneStatus', key: 'oral',
      render: (v) => <Tag color={ORAL_COLOR[v]} style={{ borderRadius: 9999, fontSize: 11 }}>{v?.toUpperCase() || '—'}</Tag>,
    },
    {
      title: 'Cavities', dataIndex: 'hasCavities', key: 'cavities',
      render: (v) => <Tag color={v ? 'red' : 'green'} style={{ borderRadius: 9999, fontSize: 11 }}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Gum Issues', dataIndex: 'hasGumIssues', key: 'gum',
      render: (v) => <Tag color={v ? 'red' : 'green'} style={{ borderRadius: 9999, fontSize: 11 }}>{v ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Treatment', dataIndex: 'treatmentRequired', key: 'treatment',
      render: (v) => <Tag color={v ? 'orange' : 'green'} style={{ borderRadius: 9999, fontSize: 11 }}>{v ? 'Required' : 'Not Required'}</Tag>,
    },
    { title: 'Treatment Type', dataIndex: 'treatmentType',  key: 'ttype',     render: (v) => v || '—' },
    { title: 'Next Visit',     dataIndex: 'nextVisitDate',   key: 'nextVisit', render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—' },
    { title: 'Date',           dataIndex: 'checkupDate',     key: 'date',      render: (v) => formatDate(v) },
  ];

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: 'Doctor Checkups' },
        ]}
      />

      {/* ── Section 1: Pending ─────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <SectionToolbar
          title="Pending Checkups"
          searchValue={pendingSearch}
          onSearchChange={handlePendingSearch}
          searchPlaceholder="Search by child name, parent phone…"
          filterOpen={pendingFilterOpen}
          onFilterOpenChange={handlePendingFilterOpen}
          activeFilterCount={pendingActiveCount}
          filterContent={
            <PendingFilterPanel
              form={pendingFilterForm}
              eventOptions={eventOptions}
              onApply={applyPendingFilters}
              onReset={resetPendingFilters}
            />
          }
        />
        <DataTable
          columns={pendingColumns}
          dataSource={filteredPending}
          loading={regLoading}
          rowKey="id"
          pagination={{
            current:  pendingPagination.page,
            pageSize: pendingPagination.limit,
            total:    filteredPending.length,
            onChange: (page, limit) => setPendingPagination({ page, limit }),
          }}
        />
      </div>

      {/* ── Section 2: Completed ───────────────────────────── */}
      <div>
        <SectionToolbar
          title="Completed Checkups"
          searchValue={completedSearch}
          onSearchChange={handleCompletedSearch}
          searchPlaceholder="Search by child name, doctor…"
          filterOpen={completedFilterOpen}
          onFilterOpenChange={handleCompletedFilterOpen}
          activeFilterCount={completedActiveCount}
          filterContent={
            <CompletedFilterPanel
              form={completedFilterForm}
              eventOptions={eventOptions}
              onApply={applyCompletedFilters}
              onReset={resetCompletedFilters}
            />
          }
        />
        <DataTable
          columns={checkupColumns}
          dataSource={filteredCheckups}
          loading={checkupLoading}
          rowKey="id"
          pagination={{
            current:  completedPagination.page,
            pageSize: completedPagination.limit,
            total:    filteredCheckups.length,
            onChange: (page, limit) => setCompletedPagination({ page, limit }),
          }}
        />
      </div>

      {/* Checkup Form */}
      <CheckupForm
        open={checkupOpen}
        onClose={() => { setCheckupOpen(false); setSelectedRegistration(null); }}
        onSubmit={(values) => createMutation.mutate(values)}
        registration={selectedRegistration}
        loading={createMutation.isPending}
      />
    </div>
  );
};

export default CheckupList;
